import { defineStore } from 'pinia';

const API_BASE = '';
const SITE_CONFIG_URL = `${API_BASE}/api/v2/site-config/`;

export type SiteConfigStatus = 'uninitialized' | 'loading' | 'ready' | 'error';

export const useSiteConfigStore = defineStore('siteConfig', {
  state: () => ({
    status: 'uninitialized' as SiteConfigStatus,
    recaptchaSiteKey: '',
    recaptchaEnabled: false,
    /** GA4 measurement ID (e.g. G-XXXXXXXXXX), from site-config when configured. */
    googleAnalyticsTag: '',
    /** GA4 DebugView (gtag debug_mode); from site-config. */
    googleAnalyticsDebugMode: false,
    /** When false (default), SPA adds <meta name="robots" content="noindex, nofollow">. */
    googleIndex: false,
    /** VAPID public key for Web Push (empty if server not configured). */
    vapidPublicKey: '',
    error: null as string | null,
  }),

  actions: {
    /**
     * Load public site config from Django (runtime; not baked into the bundle).
     * Safe to call on every app load; failures leave keys empty so VITE_* fallback can apply.
     */
    async initialize(): Promise<void> {
      this.status = 'loading';
      this.error = null;
      try {
        const response = await fetch(SITE_CONFIG_URL, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`site-config: HTTP ${response.status}`);
        }
        const data = (await response.json()) as {
          recaptcha_site_key?: string;
          recaptcha_enabled?: boolean;
          google_analytics_tag?: string;
          google_analytics_debug_mode?: boolean;
          google_index?: boolean;
          vapid_public_key?: string;
        };
        this.recaptchaSiteKey =
          typeof data.recaptcha_site_key === 'string' ? data.recaptcha_site_key : '';
        this.recaptchaEnabled = Boolean(data.recaptcha_enabled);
        this.googleAnalyticsTag =
          typeof data.google_analytics_tag === 'string' ? data.google_analytics_tag.trim() : '';
        this.googleAnalyticsDebugMode = data.google_analytics_debug_mode === true;
        this.googleIndex = data.google_index === true;
        this.vapidPublicKey =
          typeof data.vapid_public_key === 'string' ? data.vapid_public_key.trim() : '';
        this.status = 'ready';
      } catch (e) {
        this.status = 'error';
        this.error = e instanceof Error ? e.message : String(e);
      }
    },
  },
});
